import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { IRunningTaskStatusItem } from "../../types/XSifterQueries";
import XSifterQueryService from "../../services/XSifterQueryService";
import { useInterval } from "./UseInterval";
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CloseIcon from '@mui/icons-material/Close';
import "./RunningTasksDisplay.css"
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';

const PaperComponent: React.FC<PaperProps> = (props: PaperProps) => {
    const nodeRef = React.useRef<HTMLDivElement>(null);
    return (
        <Draggable
        nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
        handle="#tasklist-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}
        >
        <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

interface IRunningTasksDisplayProps {
}

const RunningTasksDisplay: React.FC<IRunningTasksDisplayProps> = (props: IRunningTasksDisplayProps) => {
    const [showTaskList, setShowTaskList] = useState<boolean>(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
    const [cancelRunningTaskId, setCancelRunningTaskId] = useState<string | null>(null);
    const [taskMessage, setTaskMessage] = useState<string | null>(null);
    const [runningTasks, setRunningTasks] = useState<IRunningTaskStatusItem[]>([]);
    const queryService = new XSifterQueryService();
    let isGettingTasks: boolean = false;

    useEffect(() => {
        refreshRunningTasks(false);
    }, [])

    useInterval(() => {
        refreshRunningTasks(false);
    }, 10000);

    const refreshRunningTasks = (override: boolean) => {
        if (showCancelConfirm && !override)
            return;

        if (isGettingTasks)
            return;

        isGettingTasks = true;
        queryService.getRunningTasks()
            .then((response) => {
                setRunningTasks(response.items);
            }).catch(() => {
                // ignore; the next poll will retry, and 401s are handled globally by the auth interceptor
            }).finally(() => {
                isGettingTasks = false;
            })
    }

    const cancelRunningTask = (taskId: string) => {
        // todo - open dialog verify to cancel,
        // let user know any credits already used cannot be refunded
        setShowCancelConfirm(true);
        setCancelRunningTaskId(taskId);
    }

    const handleConfirmationClose = () => {
        setShowCancelConfirm(false);
        setCancelRunningTaskId(null);
    }

    const performCancelTask = () => {
        if (cancelRunningTaskId === null)
            return;
        queryService.cancelRunningJob(cancelRunningTaskId)
            .then((response) => {
                setTaskMessage("Task cancelled");
                setShowCancelConfirm(false);
                setCancelRunningTaskId(null);
                refreshRunningTasks(true);
            }).catch(() => {
                setTaskMessage("Could not cancel task");
                setShowCancelConfirm(false);
                setCancelRunningTaskId(null);
                refreshRunningTasks(true);
            })
    }

    const displayRunningTasksList = () => {
        if (runningTasks.length === 0) {
            return (<Typography component="span">(no tasks are running)</Typography>);
        }

        return runningTasks.map((task) => <div>
                    <Typography id={ `jobtask-{task.id}` } variant="body2">
                        {task.queryTitle}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            aria-labelledby={`jobtask-{task.id}`}
                            value={task.percentComplete * 100.0}
                            {...props}
                        />
                        </Box>
                        <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {Math.round(task.percentComplete * 100.0)} %
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: 120 }}>
                            {task.isCancelled ? <>(cancelled)</> :
                            <Button variant="outlined" color="primary" onClick={() => cancelRunningTask(task.runningJobId)}>
                                Cancel
                            </Button>}
                        </Box>
                    </Box>                    
                </div>);
    }

    const onCloseTaskListDialog = () => {
        setShowTaskList(false);
    }

    return (
        <div>
            <Tooltip title="No running tasks">
                <IconButton disabled={runningTasks.length < 0} onClick={() => setShowTaskList(true)}>
                    <Badge
                        badgeContent={runningTasks.length}
                        color="primary"
                        max={99}
                    >
                        <PlaylistAddCheckIcon />
                    </Badge>
                </IconButton>
            </Tooltip>      
            <Dialog open={showTaskList}
                onClose={onCloseTaskListDialog} 
                fullWidth={true}
                PaperComponent={PaperComponent} 
                aria-labelledby="tasklist-dialog-title"
            >
                <DialogTitle style={{ cursor: 'move' }} className="xsifter-dialogtitle-close" id="tasklist-dialog-title">
                    Running Tasks ({runningTasks.length})
                    <IconButton onClick={() => setShowTaskList(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {taskMessage && <div>{taskMessage}</div>}
                    {displayRunningTasksList()}
                </DialogContent>
            </Dialog>
            <Dialog open={showCancelConfirm}
                role="alertdialog"
                aria-labelledby="id-confirm-dialog-title"
                aria-describedby="id-config-dialog-body"
                onClose={handleConfirmationClose}
            >
                <DialogTitle id="id-confirm-dialog-title">Cancel running task?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="id-config-dialog-body">
                        Are you sure you want to cancel this task?
                        Any already used credits cannot be refunded.
                    </DialogContentText>
                    <DialogActions>
                        <Button onClick={handleConfirmationClose}>
                            No, keep running
                        </Button>
                        <Button onClick={performCancelTask}>
                            Yes, cancel this task
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default RunningTasksDisplay;
